if (!customElements.get('plan-selector')) {
  customElements.define(
    'plan-selector',
    class PlanSelector extends HTMLElement {
      connectedCallback() {
        this.planAnchorLinks = [
          {
            hash: '#two-boxes-weekly',
            linkSelector: '.button-link > a[href="#two-boxes-weekly"]',
            planKey: 'two-boxes',
            purchaseMode: 'subscription',
            targetSelector: '#two-boxes-weekly',
          },
          {
            hash: '#try-a-single-box',
            linkSelector: '.link-extra > a[href="#try-a-single-box"]',
            planKey: 'one-box',
            purchaseMode: 'one-time',
            targetSelector: '#try-a-single-box',
          },
        ];
        this.planAnchorHashTimeouts = [];
        this.cards = Array.from(this.querySelectorAll('[data-plan-card]'));
        this.compare = this.querySelector('[data-selected-compare]');
        this.price = this.querySelector('[data-selected-price]');
        this.priceSuffix = this.querySelector('[data-selected-price-suffix]');
        this.boxes = this.querySelector('[data-selected-boxes]');
        this.background = this.querySelector('[data-selected-background]');
        this.nextBackground = this.querySelector('[data-next-background]');
        this.variantInput = this.querySelector('[data-plan-variant-input]');
        this.sellingPlanInput = this.querySelector('[data-plan-selling-plan-input]');
        this.propertyInput = this.querySelector('[data-plan-property-input]');
        this.purchaseCards = Array.from(this.querySelectorAll('[data-purchase-card]'));
        this.purchaseForms = Array.from(this.querySelectorAll('[data-purchase-form]'));
        this.purchaseToggles = Array.from(this.querySelectorAll('[data-purchase-toggle]'));
        this.productForms = Array.from(this.querySelectorAll('product-form[data-purchase-form]'));
        this.purchaseMode = this.dataset.defaultPurchaseMode || 'subscription';
        this.trialTitle = this.querySelector('[data-trial-title]');
        this.trialCompare = this.querySelector('[data-trial-compare]');
        this.trialPrice = this.querySelector('[data-trial-price]');
        this.trialBoxes = this.querySelector('[data-trial-boxes]');
        this.trialVariantInput = this.querySelector('[data-trial-variant-input]');
        this.trialPropertyInput = this.querySelector('[data-trial-property-input]');
        this.submitButton = this.querySelector('.plan-selector__purchase-card--subscribe [type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.trialSubmitButton = this.querySelector('.plan-selector__purchase-card--trial [type="submit"]');
        this.trialSubmitButtonText = this.trialSubmitButton?.querySelector('span');
        this.productOneVariantId = this.dataset.productOneVariantId;
        this.productTwoVariantId = this.dataset.productTwoVariantId;
        this.sellingPlanId = this.dataset.sellingPlanId;
        this.quantityPerBox = Number.parseInt(this.dataset.quantityPerBox, 10) || 4;
        this.productsReady = this.dataset.productsReady === 'true';
        this.productsAvailable = this.dataset.productsAvailable === 'true';
        this.subscriptionEnabled = this.dataset.subscriptionEnabled === 'true';
        this.trialAddLabel = this.hasAttribute('data-trial-add-label') ? this.dataset.trialAddLabel : this.dataset.addLabel;
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.plans = this.buildPlans();
        this.oneTimePlan = this.plans['one-box'];
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        Object.values(this.plans).forEach((plan) => {
          if (!plan.background) return;
          const image = new Image();
          if (plan.backgroundSrcset) image.srcset = plan.backgroundSrcset;
          if (plan.backgroundSizes) image.sizes = plan.backgroundSizes;
          image.src = plan.background;
        });

        this.cards.forEach((card) => {
          card.addEventListener('click', () => this.selectPlan(card.dataset.plan));
        });

        this.purchaseToggles.forEach((toggle) => {
          toggle.addEventListener('click', () => this.selectPurchaseMode(toggle.dataset.purchaseMode));
        });

        this.purchaseCards.forEach((card) => {
          card.addEventListener('click', (event) => {
            if (event.target.closest('form, product-form, [type="submit"]')) return;
            this.selectPurchaseMode(card.dataset.purchaseMode);
          });
        });

        this.productForms.forEach((productForm) => {
          productForm.querySelector('form')?.addEventListener(
            'submit',
            (event) => this.onProductFormSubmit(event, productForm),
            { capture: true }
          );
        });

        this.selectPlan(this.dataset.defaultPlan || 'three-boxes');
        this.setupPlanAnchorLinks();
      }

      disconnectedCallback() {
        this.planAnchorLinkController?.abort();
        this.planAnchorHashTimeouts.forEach((timeout) => window.clearTimeout(timeout));
      }

      buildPlans() {
        return this.cards.reduce((plans, card) => {
          const key = card.dataset.plan;
          if (plans[key]) return plans;

          plans[key] = {
            available: card.dataset.available === 'true',
            background: card.dataset.planBackground,
            backgroundAlt: card.dataset.planBackgroundAlt,
            backgroundSizes: card.dataset.planBackgroundSizes,
            backgroundSrcset: card.dataset.planBackgroundSrcset,
            compare: card.dataset.planCompare,
            key: key,
            meta: card.dataset.planMeta,
            multiplier: Number.parseInt(card.dataset.planMultiplier, 10) || 1,
            oneTimeCompare: card.dataset.planOneTimeCompare,
            oneTimePrice: card.dataset.planOneTimePrice,
            price: card.dataset.planPrice,
            selectable: !card.disabled,
            title: card.dataset.planTitle,
          };

          return plans;
        }, {});
      }

      selectPlan(planKey) {
        const selected = this.plans[planKey];
        if (!selected?.selectable) {
          const fallback = Object.entries(this.plans).find(([, plan]) => plan.selectable);
          if (!fallback) {
            this.updateSubmitState(null);
            this.updateTrialState(null);
            this.selectPurchaseMode(this.purchaseMode);
            return;
          }

          this.selectPlan(fallback[0]);
          return;
        }

        const shouldAnimate = this.currentPlan?.key && this.currentPlan.key !== selected.key && !this.prefersReducedMotion();
        this.currentPlan = selected;

        this.cards.forEach((card) => {
          const isSelected = card.dataset.plan === planKey;
          card.classList.toggle('is-selected', isSelected);
          card.setAttribute('aria-pressed', String(isSelected));
        });

        this.updateBackground(
          selected.background,
          selected.backgroundSrcset,
          selected.backgroundSizes,
          selected.backgroundAlt,
          shouldAnimate
        );
        if (this.variantInput) this.variantInput.value = this.productOneVariantId || '';
        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = this.sellingPlanId || '';
          this.sellingPlanInput.disabled = !this.sellingPlanId;
        }
        if (this.propertyInput) this.propertyInput.value = selected.meta || selected.title || '';

        const updateDetails = () => {
          if (this.compare) {
            this.compare.textContent = selected.compare || '';
            this.compare.hidden = !selected.compare;
          }

          if (this.price) this.price.textContent = selected.price || this.dataset.unavailableLabel;
          if (this.priceSuffix) this.priceSuffix.hidden = !selected.price;
          if (this.boxes) this.boxes.textContent = selected.meta || '';

          this.updateSubmitState(selected);
          this.updateTrialState(selected);
          this.selectPurchaseMode(this.purchaseMode);
        };

        if (shouldAnimate) {
          this.animatePlanDetails(updateDetails);
        } else {
          updateDetails();
        }
      }

      selectPurchaseMode(mode) {
        const nextMode = mode || 'subscription';
        const shouldAnimate = this.purchaseMode !== nextMode && !this.prefersReducedMotion();
        if (shouldAnimate) {
          window.clearTimeout(this.modeAnimationTimeout);
          this.classList.add('is-mode-switching');
          this.modeAnimationTimeout = window.setTimeout(() => this.classList.remove('is-mode-switching'), 240);
        }

        this.purchaseMode = nextMode;

        this.purchaseCards.forEach((card) => {
          const isSelected = card.dataset.purchaseMode === this.purchaseMode;
          card.classList.toggle('is-purchase-selected', isSelected);
        });

        this.purchaseToggles.forEach((toggle) => {
          toggle.setAttribute('aria-pressed', String(toggle.dataset.purchaseMode === this.purchaseMode));
        });

        this.purchaseForms.forEach((form) => {
          form.hidden = form.dataset.purchaseForm !== this.purchaseMode;
        });
      }

      setupPlanAnchorLinks() {
        this.planAnchorLinkController?.abort();
        this.planAnchorLinkController = new AbortController();
        this.planAnchorHashTimeouts = [];

        this.planAnchorLinks.forEach((config) => {
          const links = Array.from(document.querySelectorAll(config.linkSelector));
          if (!links.length && window.location.hash !== config.hash) return;

          links.forEach((link) => {
            link.addEventListener('click', (event) => this.openPlanAnchor(config, event), {
              signal: this.planAnchorLinkController.signal,
            });
          });

          if (window.location.hash === config.hash) {
            this.planAnchorHashTimeouts.push(window.setTimeout(() => this.openPlanAnchor(config), 0));
          }
        });
      }

      openPlanAnchor(config, event) {
        //event?.preventDefault();
        this.selectPlan(config.planKey);

        if (this.purchaseCards.some((card) => card.dataset.purchaseMode === config.purchaseMode)) {
          this.selectPurchaseMode(config.purchaseMode);
        }

        /*const target = this.querySelector(config.targetSelector) || this;
        const scrollOffset = 100;
        const targetTop = target.getBoundingClientRect().top + window.pageYOffset - scrollOffset;
        window.scrollTo({
          top: Math.max(targetTop, 0),
          behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        });*/

        if (window.location.hash === config.hash) {
          window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        }
      }

      animatePlanDetails(updateDetails) {
        window.clearTimeout(this.planAnimationTimeout);
        window.clearTimeout(this.planAnimationCleanupTimeout);
        this.classList.remove('is-plan-entering');
        this.classList.add('is-plan-exiting');

        this.planAnimationTimeout = window.setTimeout(() => {
          updateDetails();
          this.classList.remove('is-plan-exiting');
          this.classList.add('is-plan-entering');

          requestAnimationFrame(() => {
            this.classList.remove('is-plan-entering');
            this.planAnimationCleanupTimeout = window.setTimeout(() => {
              this.classList.remove('is-plan-exiting', 'is-plan-entering');
            }, 260);
          });
        }, 90);
      }

      updateBackground(backgroundSrc, backgroundSrcset, backgroundSizes, backgroundAlt, shouldAnimate) {
        if (!this.background || !backgroundSrc) return;
        if (!shouldAnimate || !this.nextBackground || this.background.dataset.currentBackground === backgroundSrc) {
          this.setBackgroundImage(this.background, backgroundSrc, backgroundSrcset, backgroundSizes, backgroundAlt);
          if (this.nextBackground)
            this.setBackgroundImage(this.nextBackground, backgroundSrc, backgroundSrcset, backgroundSizes, backgroundAlt);
          return;
        }

        window.clearTimeout(this.backgroundAnimationTimeout);
        this.setBackgroundImage(this.nextBackground, backgroundSrc, backgroundSrcset, backgroundSizes, backgroundAlt);
        this.classList.remove('is-background-resetting');
        this.nextBackground.classList.add('is-visible');
        this.background.classList.add('is-fading');

        this.backgroundAnimationTimeout = window.setTimeout(() => {
          this.classList.add('is-background-resetting');
          this.setBackgroundImage(this.background, backgroundSrc, backgroundSrcset, backgroundSizes, backgroundAlt);
          this.background.classList.remove('is-fading');
          this.nextBackground.classList.remove('is-visible');
          this.setBackgroundImage(this.nextBackground, backgroundSrc, backgroundSrcset, backgroundSizes, backgroundAlt);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.classList.remove('is-background-resetting');
            });
          });
        }, 620);
      }

      setBackgroundImage(image, backgroundSrc, backgroundSrcset, backgroundSizes, backgroundAlt) {
        if (!image || !backgroundSrc) return;

        if (backgroundSrcset) {
          image.srcset = backgroundSrcset;
        } else {
          image.removeAttribute('srcset');
        }

        if (backgroundSizes) {
          image.sizes = backgroundSizes;
        } else {
          image.removeAttribute('sizes');
        }

        image.src = backgroundSrc;
        image.alt = image.hasAttribute('aria-hidden') ? '' : backgroundAlt || '';
        image.dataset.currentBackground = backgroundSrc;
      }

      prefersReducedMotion() {
        return this.motionQuery?.matches;
      }

      updateSubmitState(plan) {
        if (!this.submitButton || !this.submitButtonText) return;

        const disabled = !plan || !this.productsReady || !this.productsAvailable || !this.subscriptionEnabled;
        this.submitButton.disabled = disabled;
        this.submitButtonText.textContent = this.labelForPlan(plan);
      }

      updateTrialState(plan) {
        const oneTimePlan = this.oneTimePlan || plan;

        if (this.trialTitle) this.trialTitle.textContent = this.dataset.trialPlanTitle || oneTimePlan?.title || '';
        if (this.trialBoxes) this.trialBoxes.textContent = oneTimePlan?.meta || '';
        if (this.trialPrice) this.trialPrice.textContent = oneTimePlan?.oneTimePrice || this.dataset.unavailableLabel;

        if (this.trialCompare) {
          this.trialCompare.textContent = oneTimePlan?.oneTimeCompare || '';
          this.trialCompare.hidden = !oneTimePlan?.oneTimeCompare;
        }

        if (this.trialVariantInput) this.trialVariantInput.value = this.productOneVariantId || '';
        if (this.trialPropertyInput) this.trialPropertyInput.value = this.dataset.trialPlanTitle || oneTimePlan?.meta || '';
        if (!this.trialSubmitButton || !this.trialSubmitButtonText) return;

        const disabled = !oneTimePlan || !this.productsReady || !this.productsAvailable;
        this.trialSubmitButton.disabled = disabled;
        this.trialSubmitButtonText.textContent = this.labelForOneTimePlan(oneTimePlan);
      }

      labelForPlan(plan) {
        if (!plan || !this.productsReady || !this.subscriptionEnabled) return this.dataset.unavailableLabel;
        if (!this.productsAvailable) return this.dataset.soldOutLabel;
        return this.dataset.addLabel;
      }

      labelForOneTimePlan(plan) {
        if (!plan || !this.productsReady || !this.productsAvailable) return this.dataset.unavailableLabel;
        return this.trialAddLabel;
      }

      onProductFormSubmit(event, productForm) {
        const plan = this.currentPlan;
        if (!plan || !this.productsReady) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.submitPlan(productForm, productForm.dataset.purchaseForm || this.purchaseMode, plan);
      }

      async submitPlan(productForm, mode, plan) {
        const submitButton = productForm.querySelector('[type="submit"]');
        const spinner = productForm.querySelector('.loading__spinner');
        if (!submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;
        const purchasePlan = mode === 'one-time' ? this.oneTimePlan || plan : plan;

        this.handlePlanError(productForm);
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
        spinner?.classList.remove('hidden');

        const payload = {
          items: this.buildPlanItems(purchasePlan, mode),
        };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement?.(document.activeElement);
        }

        try {
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });
          const cartResponse = await response.json();

          if (cartResponse.status) {
            this.publishCartError(cartResponse);
            throw new Error(cartResponse.description || cartResponse.message || this.dataset.unavailableLabel);
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.normalizeCartResponse(cartResponse);
          await this.publishCartUpdate(cartResponse);
          this.cart.renderContents(cartResponse);
        } catch (error) {
          this.handlePlanError(productForm, error.message);
        } finally {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        }
      }

      buildPlanItems(plan, mode) {
        const derivedQuantity = Math.max(1, this.quantityPerBox * plan.multiplier);
        if (!this.productOneVariantId || !this.productTwoVariantId) return [];

        const productOneItem = {
          id: this.productOneVariantId,
          quantity: derivedQuantity,
        };

        const productTwoItem = {
          id: this.productTwoVariantId,
          quantity: derivedQuantity,
        };

        if (mode === 'subscription' && this.sellingPlanId) {
          productOneItem.selling_plan = this.sellingPlanId;
          productTwoItem.selling_plan = this.sellingPlanId;
        }

        return [productOneItem, productTwoItem];
      }

      normalizeCartResponse(cartResponse) {
        if (!cartResponse.key && cartResponse.items?.[0]?.key) {
          cartResponse.key = cartResponse.items[0].key;
        }
      }

      publishCartUpdate(cartResponse) {
        if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return Promise.resolve();

        return publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: this.productOneVariantId,
          cartData: cartResponse,
        });
      }

      publishCartError(cartResponse) {
        if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: this.productOneVariantId,
          errors: cartResponse.errors || cartResponse.description,
          message: cartResponse.message,
        });
      }

      handlePlanError(productForm, errorMessage = false) {
        const errorWrapper = productForm.querySelector('.product-form__error-message-wrapper');
        const errorText = productForm.querySelector('.product-form__error-message');
        if (!errorWrapper || !errorText) return;

        errorWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) errorText.textContent = errorMessage;
      }
    }
  );
}
